import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  IconButton, 
  Tooltip
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useTheme, styled } from '@mui/material/styles';
import { APP_VERSION } from '../version';

// Stilisiertes Zahnrad-Icon mit Hover-Effekt
const RotatingSettingsIcon = styled(SettingsIcon)(({ theme }) => ({
  transition: 'transform 0.5s ease',
  '&:hover': {
    transform: 'rotate(90deg)',
  },
}));

// Stilisiertes Logo
const Logo = styled('img')(({ theme }) => ({
  height: '40px',
  marginRight: theme.spacing(2),
  cursor: 'pointer',
}));

// Stilisiertes Glühbirnen-Icon
const BulbIcon = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  width: 30,
  height: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  }
}));

// Angepasste AppBar ohne Overlay-Effekt
const CustomAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: 'var(--primary-main)',
  backgroundImage: 'none', // Entfernt den Gradient-Overlay
  '&::before': {
    display: 'none', // Entfernt zusätzliche Pseudo-Elemente die Overlays hinzufügen könnten
  },
  '&.MuiPaper-root': {
    '--Paper-overlay': 'none', // Überschreibt die Paper-Overlay-Variable
  }
}));

const Header = ({ darkMode, toggleDarkMode }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isSettingsPage = location.pathname === '/settings';
  const [hoverBulb, setHoverBulb] = useState(false);

  // Speichere den vorherigen Pfad, wenn nicht auf der Einstellungsseite
  useEffect(() => {
    if (location.pathname !== '/settings') {
      sessionStorage.setItem('previousPath', location.pathname);
    }
  }, [location.pathname]);

  // Funktion zum Behandeln des Klicks auf das Einstellungsicon
  const handleSettingsClick = (event) => {
    event.preventDefault();
    
    if (isSettingsPage) {
      // Auf der Einstellungsseite, gehe zurück zur vorherigen Seite
      const previousPath = sessionStorage.getItem('previousPath') || '/';
      navigate(previousPath);
    } else {
      // Nicht auf der Einstellungsseite, gehe zu den Einstellungen
      navigate('/settings');
    }
  };

  return (
    <CustomAppBar position="static">
      <Toolbar>        {/* NIGHTOWL Logo als Link zur Vergleichsseite */}        <Box component={Link} to="/" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <Logo src="/images/Nightowl.png" alt="NIGHTOWL Logo" />
          <Box 
            component="small" 
            sx={{ 
              opacity: 0.8, 
              fontSize: '0.75rem', 
              color: theme.palette.mode === 'dark' ? 'white' : 'rgba(255, 255, 255, 0.9)',
              ml: -1.5,
              alignSelf: 'flex-end',
              mb: 0.5
            }}
          >
            v{APP_VERSION}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={darkMode ? "Zum Light Mode wechseln" : "Zum Dark Mode wechseln"}>
            <BulbIcon
              onMouseEnter={() => setHoverBulb(true)}
              onMouseLeave={() => setHoverBulb(false)}
              onClick={toggleDarkMode}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                {/* Glüh-Effekt Hintergrund - nur sichtbar bei leuchtender Birne im Light Mode */}
                {!darkMode && (
                  <circle 
                    cx="12" 
                    cy="11" 
                    r="7" 
                    fill={hoverBulb ? "transparent" : "rgba(255, 215, 0, 0.3)"}
                    style={{ filter: hoverBulb ? "none" : "blur(5px)", transition: "all 0.3s ease" }}
                  />
                )}
                
                {/* Lichtstrahlen - nur sichtbar wenn sie angeschaltet aussehen soll */}
                {((!darkMode && !hoverBulb) || (darkMode && hoverBulb)) && (
                  <>
                    <line x1="12" y1="2" x2="12" y2="4" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="22" y1="12" x2="20" y2="12" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="19.78" y1="19.78" x2="18.36" y2="18.36" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="12" y1="22" x2="12" y2="20" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="2" y1="12" x2="4" y2="12" stroke="#FFD700" strokeWidth="0.8" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="#FFD700" strokeWidth="0.8" />
                  </>
                )}
                
                {/* Glühbirnen-Körper */}
                <path 
                  d="M9,21h6v-2h-6V21z M12,3c-4.42,0-8,3.58-8,8c0,2.48,1.13,4.7,2.91,6.17l1.42-1.43 C6.94,14.55,6,12.85,6,11c0-3.31,2.69-6,6-6s6,2.69,6,6c0,1.85-0.94,3.55-2.33,4.74l1.42,1.43C18.87,15.7,20,13.48,20,11 C20,6.58,16.42,3,12,3z" 
                  fill={(() => {
                    if (darkMode) {
                      // Im Dark Mode: Standardmäßig grau, beim Hover gelb
                      return hoverBulb ? "#FFD700" : "#78909c";
                    } else {
                      // Im Light Mode: Standardmäßig gelb, beim Hover grau
                      return hoverBulb ? "#78909c" : "#FFD700";
                    }
                  })()}
                  style={{ transition: "fill 0.3s ease" }}
                />
                
                {/* Glühbirnen-Basis */}
                <path 
                  d="M9,19h6v-1.5h-6V19z M10,22h4v-1h-4V22z" 
                  fill="#455a64"
                />
              </svg>
            </BulbIcon>
          </Tooltip>
          
          <IconButton 
            color="inherit"
            onClick={handleSettingsClick}
            aria-label={isSettingsPage ? "Zurück" : "Einstellungen"}
          >
            <RotatingSettingsIcon />
          </IconButton>
        </Box>
      </Toolbar>
    </CustomAppBar>
  );
};

export default Header;